import React from "react";
import { ChevronDown, Share, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const FORMAT_OPTIONS = ["CSV", "Excel"] as const;
type ExportFormat = (typeof FORMAT_OPTIONS)[number];

interface ExportFilterPopoverProps {
  selectedFormat: ExportFormat | null;
  onApply: (format: ExportFormat | null) => void;
}

export const ExportFilterPopover = ({
  selectedFormat,
  onApply,
}: ExportFilterPopoverProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draftFormat, setDraftFormat] = React.useState<ExportFormat | null>(
    null,
  );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setDraftFormat(selectedFormat);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="span"
          aria-label={t("billing.export")}
          className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-medium text-zinc-900"
        >
          <Share className="size-4" />
          {t("billing.export")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[320px] rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-medium leading-normal text-[#171717]">
              {t("billing.exportFilter.title")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("billing.exportFilter.close")}
            >
              <X className="size-5" />
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-base font-medium leading-normal text-zinc-600">
              {t("billing.exportFilter.format")}
              <ChevronDown className="size-4 rotate-180 text-zinc-500" />
            </div>

            <Select
              value={draftFormat ?? undefined}
              onValueChange={(value) => setDraftFormat(value as ExportFormat)}
            >
              <SelectTrigger
                showIcon={false}
                className="h-9 w-full justify-between rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-base font-medium leading-normal text-zinc-600 shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]"
              >
                <SelectValue placeholder={t("billing.exportFilter.format")} />
                <ChevronDown className="size-4 text-zinc-500" />
              </SelectTrigger>
              <SelectContent className="border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
                {FORMAT_OPTIONS.map((format) => (
                  <SelectItem
                    key={format}
                    value={format}
                    className={cn(
                      "min-h-8 rounded-sm px-2 py-1.5 text-sm font-normal leading-normal text-[#171717]",
                      draftFormat === format && "bg-[#F5F5F5]",
                    )}
                  >
                    {format}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-end gap-1">
            <Button
              type="button"
              className="h-8 w-fit rounded-md bg-[#6D82A3] px-4 py-2 text-sm font-medium leading-normal text-[#FAFAFA] hover:bg-[#5F7597]"
              onClick={() => {
                onApply(draftFormat);
                setOpen(false);
              }}
            >
              {t("billing.exportFilter.exportFile")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
